/**
 * Per-chunk timing index: O(log n) lookups from a playback position to the
 * active doc-pack anchor / legacy word, replacing the O(n) linear scans that
 * `getDocPackPlaybackAnchorForPosition` (`./core`) used to run on every tick.
 *
 * Pure TS — no DOM, no React, no Effect — so it can be shared by the web
 * highlight manager (a non-render consumer), the mobile screens, and the
 * follow adapters, and tested in the node vitest environment.
 *
 * Chunk objects come from react-query caches with `staleTime: Infinity`, so
 * indexing by object identity in a WeakMap is safe (`getChunkTimingIndex`).
 */

import type { DocPackPageCharRange } from '@listening/doc-pack';
import type {
  SynthesizeWordTiming,
  TtsTimestampMs,
} from '@listening/api-contract/schemas';
import { findActiveWordIdx } from './alignment';

/** The fields of a TTS chunk the timing index reads. Both `ChapterTtsChunk`
 * (legacy: only `alignment_timestamps`) and `DocPackTtsChunk` (doc-pack: adds
 * timings + reading chunk) are structurally assignable — callers pass their
 * chunk directly, no cast. */
export type ChunkTimingSource = {
  readonly alignment_timestamps?: ReadonlyArray<TtsTimestampMs>;
  readonly docPackWordTimings?: ReadonlyArray<SynthesizeWordTiming>;
  readonly docPackChunk?: {
    readonly ranges: ReadonlyArray<DocPackPageCharRange>;
  };
};

/** A playback position resolved to the doc-pack range it sits in. */
export type DocPackPlaybackAnchor = {
  readonly page: number;
  readonly charStart: number;
  readonly charEnd: number;
};

export type ChunkTimingIndex = {
  /** Anchor for the position: active ?? previous ?? next, then the first-range
   * fallback — identical semantics to the old linear scan. */
  readonly docPackAnchorAt: (
    positionMs: number,
  ) => DocPackPlaybackAnchor | undefined;
  /** Every timing overlapping the position (the web manager stamps them all). */
  readonly activeDocPackTimingsAt: (
    positionMs: number,
  ) => ReadonlyArray<SynthesizeWordTiming>;
  /** Legacy `alignment_timestamps` active-word index; -1 = before first / none. */
  readonly activeWordIdxAt: (positionMs: number) => number;
  /** Count of legacy timings; `null` = no alignment → "untimed word" rule. */
  readonly legacyTimingCount: number | null;
  /** Start time of the legacy word at `idx`; undefined out of range. */
  readonly legacyWordStartMsAt: (idx: number) => number | undefined;
  /** Start time of the timing an anchor was resolved from; undefined when the
   * anchor came from the first-range fallback rather than a timing. */
  readonly docPackAnchorStartMsAt: (
    anchor: DocPackPlaybackAnchor,
  ) => number | undefined;
};

/** Largest index `i` with `values[i] <= target`, or -1. `values` sorted asc. */
function largestLE(values: readonly number[], target: number): number {
  let lo = 0;
  let hi = values.length - 1;
  let res = -1;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const v = values[mid];
    if (v !== undefined && v <= target) {
      res = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return res;
}

/** Smallest index `i` with `values[i] >= target`, or `values.length`. Sorted asc. */
function smallestGE(values: readonly number[], target: number): number {
  let lo = 0;
  let hi = values.length - 1;
  let res = values.length;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const v = values[mid];
    if (v !== undefined && v >= target) {
      res = mid;
      hi = mid - 1;
    } else lo = mid + 1;
  }
  return res;
}

const EMPTY_TIMINGS: ReadonlyArray<SynthesizeWordTiming> = [];

const EMPTY_INDEX: ChunkTimingIndex = {
  docPackAnchorAt: () => undefined,
  activeDocPackTimingsAt: () => EMPTY_TIMINGS,
  activeWordIdxAt: () => -1,
  legacyTimingCount: null,
  legacyWordStartMsAt: () => undefined,
  docPackAnchorStartMsAt: () => undefined,
};

function buildIndex(chunk: ChunkTimingSource): ChunkTimingIndex {
  const timings = chunk.docPackWordTimings ?? EMPTY_TIMINGS;
  const firstRange = chunk.docPackChunk?.ranges[0];

  // Sorted views + a prefix "argmax end" so active/previous/next each resolve in
  // O(log n) even when timings overlap.
  const byStart = timings.toSorted((a, b) => a.start_ms - b.start_ms);
  const byEnd = timings.toSorted((a, b) => a.end_ms - b.end_ms);
  const startKeys = byStart.map((t) => t.start_ms);
  const endKeys = byEnd.map((t) => t.end_ms);
  const argMaxEndPrefix: number[] = [];
  let best = -1;
  for (let i = 0; i < byStart.length; i++) {
    const cur = byStart[i];
    const bestEntry = best >= 0 ? byStart[best] : undefined;
    if (!bestEntry || (cur && cur.end_ms > bestEntry.end_ms)) best = i;
    argMaxEndPrefix.push(best);
  }

  const resolveTiming = (
    positionMs: number,
  ): SynthesizeWordTiming | undefined => {
    // active: some timing with start <= pos <= end. The prefix argmax-end word
    // is in {start <= pos} and, if its end >= pos, contains the position.
    const startLE = largestLE(startKeys, positionMs);
    if (startLE >= 0) {
      const argIdx = argMaxEndPrefix[startLE];
      const cand = argIdx === undefined ? undefined : byStart[argIdx];
      if (cand && cand.end_ms >= positionMs) return cand;
    }
    // previous: latest-ending timing that finished at/before the position.
    const endLE = largestLE(endKeys, positionMs);
    if (endLE >= 0) return byEnd[endLE];
    // next: earliest-starting timing at/after the position.
    return byStart[smallestGE(startKeys, positionMs)];
  };

  const docPackAnchorAt = (
    positionMs: number,
  ): DocPackPlaybackAnchor | undefined => {
    const timing = resolveTiming(positionMs);
    if (timing) {
      // A null-page (raw-text) timing borrows the chunk's first-range page so
      // its char offsets still produce a usable anchor.
      const page = timing.page ?? firstRange?.[0];
      if (page !== undefined) {
        return { page, charStart: timing.char_start, charEnd: timing.char_end };
      }
    }
    if (!firstRange) return undefined;
    return {
      page: firstRange[0],
      charStart: firstRange[1],
      charEnd: firstRange[2],
    };
  };

  const activeDocPackTimingsAt = (
    positionMs: number,
  ): ReadonlyArray<SynthesizeWordTiming> =>
    timings.filter((t) => t.start_ms <= positionMs && positionMs <= t.end_ms);

  const alignment = chunk.alignment_timestamps ?? [];

  let anchorStartMsByKey: Map<string, number> | null = null;
  const docPackAnchorStartMsAt = (anchor: DocPackPlaybackAnchor) => {
    if (anchorStartMsByKey === null) {
      anchorStartMsByKey = new Map();
      for (const timing of timings) {
        const page = timing.page ?? firstRange?.[0];
        if (page === undefined) continue;
        const key = `${page}:${timing.char_start}`;
        if (!anchorStartMsByKey.has(key)) {
          anchorStartMsByKey.set(key, timing.start_ms);
        }
      }
    }
    return anchorStartMsByKey.get(`${anchor.page}:${anchor.charStart}`);
  };

  return {
    docPackAnchorAt,
    activeDocPackTimingsAt,
    activeWordIdxAt: (positionMs) => findActiveWordIdx(alignment, positionMs),
    legacyTimingCount: alignment.length > 0 ? alignment.length : null,
    legacyWordStartMsAt: (idx) => alignment[idx]?.start,
    docPackAnchorStartMsAt,
  };
}

const cache = new WeakMap<ChunkTimingSource, ChunkTimingIndex>();

/** Cached-by-identity timing index for a chunk. `null`/`undefined` → empty index. */
export function getChunkTimingIndex(
  chunk: ChunkTimingSource | null | undefined,
): ChunkTimingIndex {
  if (!chunk) return EMPTY_INDEX;
  const cached = cache.get(chunk);
  if (cached) return cached;
  const built = buildIndex(chunk);
  cache.set(chunk, built);
  return built;
}

/** Anchor for a playback position within a doc-pack chunk. The index is
 * WeakMap-cached per chunk, so every caller — the mobile screen, mini-player,
 * pdf-reader, and the web follow adapters — gets the O(log n) path. */
export function getDocPackPlaybackAnchorForPosition(
  audioChunk: ChunkTimingSource | null | undefined,
  positionMs: number,
): DocPackPlaybackAnchor | undefined {
  return getChunkTimingIndex(audioChunk).docPackAnchorAt(positionMs);
}
